import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { PlusCircle, Search, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { STATUS_OPTIONS, SUBJECT_OPTIONS, getStatusColor, getStatusLabel, getSubjectLabel } from '../lib/subjectFields';

export default function LeadsListPage() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const page = parseInt(searchParams.get('page') || '1');
  const status = searchParams.get('status') || '';
  const subject = searchParams.get('subject') || '';
  const search = searchParams.get('search') || '';

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (status) params.status = status;
      if (subject) params.subject = subject;
      if (search) params.search = search;
      const res = await API.get('/leads', { params });
      setLeads(res.data.leads);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, status, subject, search]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const updateFilter = (key, val) => {
    const p = new URLSearchParams(searchParams);
    if (val) p.set(key, val); else p.delete(key);
    if (key !== 'page') p.set('page', '1');
    setSearchParams(p);
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (status) params.status = status;
      if (subject) params.subject = subject;
      const res = await API.get('/leads/export/csv', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'leads_export.csv';
      a.click();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="space-y-4 fade-in" data-testid="leads-list-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#0B1120]">Leads</h1>
          <p className="text-sm text-muted-foreground">{total} lead{total > 1 ? 's' : ''} au total</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-csv-btn">
            <Download size={14} className="mr-1" /> Export CSV
          </Button>
          <Button className="bg-[#397DFF] hover:bg-[#2563EB] text-white gap-1" size="sm"
            onClick={() => navigate('/leads/new')} data-testid="new-lead-btn-list">
            <PlusCircle size={14} /> Nouveau lead
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border p-3">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Rechercher (client, ville, email...)"
              className="pl-9 h-9 text-sm"
              value={search}
              onChange={e => updateFilter('search', e.target.value)}
              data-testid="leads-search-input"
            />
          </div>
          <Select value={status} onValueChange={v => updateFilter('status', v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="leads-status-filter">
              <SelectValue placeholder="Tous les statuts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={subject} onValueChange={v => updateFilter('subject', v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="leads-subject-filter">
              <SelectValue placeholder="Tous les sujets" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les sujets</SelectItem>
              {SUBJECT_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card className="border shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs font-semibold">Client</TableHead>
              <TableHead className="text-xs font-semibold">Sujet</TableHead>
              <TableHead className="text-xs font-semibold">Ville</TableHead>
              <TableHead className="text-xs font-semibold">Statut</TableHead>
              <TableHead className="text-xs font-semibold">Cree par</TableHead>
              <TableHead className="text-xs font-semibold">Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Chargement...</TableCell></TableRow>
            ) : leads.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Aucun lead trouve</TableCell></TableRow>
            ) : leads.map(lead => (
              <TableRow
                key={lead.id}
                className="cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => navigate(`/leads/${lead.id}`)}
                data-testid={`lead-row-${lead.id}`}
              >
                <TableCell className="font-medium text-sm">{lead.client_name}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-[10px]">{getSubjectLabel(lead.subject)}</Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{lead.city}</TableCell>
                <TableCell>
                  <Badge className="text-[10px]" style={{ backgroundColor: getStatusColor(lead.status), color: '#fff' }}>
                    {getStatusLabel(lead.status)}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{lead.created_by_name}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {new Date(lead.created_at).toLocaleDateString('fr-FR')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Page {page} sur {pages}</p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1}
              onClick={() => updateFilter('page', String(page - 1))} data-testid="leads-prev-page">
              <ChevronLeft size={14} />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= pages}
              onClick={() => updateFilter('page', String(page + 1))} data-testid="leads-next-page">
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
